'use client'

import { Checkbox, FieldDescription, FieldLabel } from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

interface ConsentFieldProps {
  id: string
  /** A parent declares guardianship; a school declares authority to act for the guardian. */
  type: 'web' | 'school'
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
}

/**
 * Creating an account for a minor needs someone to state they are entitled to do it.
 *
 * The gate is client-side only: the declaration is about the person filling the form, not a
 * per-child record, so there is nothing to persist alongside the account.
 */
export function ConsentField({ id, type, checked, onCheckedChange, disabled }: ConsentFieldProps) {
  return (
    // `FieldLabel` styles a nested `Field` as a card, but only recolours it on `data-state=checked`,
    // which Base UI never sets (it uses `data-checked`). Own the box here instead of nesting a
    // `Field` whose built-in `border-outline` would outrank the checked border.
    <FieldLabel
      htmlFor={id}
      className={cn(
        'srd:border-outline srd:bg-grey-10 srd:w-full srd:items-start srd:gap-3 srd:rounded-lg srd:border srd:p-4',
      )}
    >
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
      <FieldDescription className='srd:text-neutral-black'>
        {type === 'web' ? (
          <>
            Saya menyatakan bahwa saya adalah orang tua/wali yang sah dan memberikan persetujuan atas pengumpulan,
            penggunaan, dan pengelolaan data anak sebagaimana dijelaskan dalam{' '}
          </>
        ) : (
          <>
            Saya menyatakan bahwa saya berwenang mewakili sekolah untuk mendaftarkan murid dan menyetujui pembuatan akun
            tersebut untuk tujuan pendidikan. Saya telah atau akan memberitahukan orang tua/wali mengenai penggunaan
            layanan ini sesuai{' '}
          </>
        )}
        <a
          href={buildUrl('marketing', '/syarat-dan-ketentuan').href}
          target='_blank'
          rel='noreferrer'
          className='srd:text-blue-100'
          // The links sit inside the label, so a bare click would toggle the checkbox too.
          onClick={(e) => e.stopPropagation()}
        >
          Ketentuan Layanan
        </a>{' '}
        dan{' '}
        <a
          href={buildUrl('marketing', '/kebijakan-privasi').href}
          target='_blank'
          rel='noreferrer'
          className='srd:text-blue-100'
          onClick={(e) => e.stopPropagation()}
        >
          Kebijakan Privasi
        </a>
        .
      </FieldDescription>
    </FieldLabel>
  )
}
